import { useMemo } from 'react';
import snakeCase from 'lodash/snakeCase';

import type { VehicleFeatures } from 'Pages/vehicles/helpers/vehicle.schema';
import { formatText } from 'Utilities/formatters';

import type { UseVehicleFeatures, UseVehicleFeaturesParams } from './useVehicleFeatures.types';

export const FEATURE_TYPE = {
	additional: 'additional',
	standard: 'standard',
} as const;

const EMPTY_FEATURE_PRICE = '-';

const getStandardFeatures = (equipment: VehicleFeatures['equipment']) =>
	equipment?.split(',').map((item) => ({
		label: formatText(item),
		name: item,
		price: EMPTY_FEATURE_PRICE,
		type: FEATURE_TYPE.standard,
	}));

const getAdditionalFeatures = (additionalSpec: VehicleFeatures['highValueCarFeatures']) =>
	additionalSpec?.items
		.filter(({ price }) => Number(price) !== 0)
		.sort((a, b) => (b?.price && a?.price ? b.price - a.price : 0))
		.map(({ name, price }) => ({
			label: name,
			name: snakeCase(name),
			price: `£${formatText(price)}`,
			type: FEATURE_TYPE.additional,
		}));

const getTotalCost = (totalCost?: number) =>
	typeof totalCost === 'number' ? `£${formatText(totalCost)}` : EMPTY_FEATURE_PRICE;

export const useVehicleFeatures = ({
	equipment,
	highValueCarFeatures,
}: UseVehicleFeaturesParams): UseVehicleFeatures => {
	// equipment comes in following format "sat_nav,heated_seats"
	const standardFeatures = useMemo(() => getStandardFeatures(equipment), [equipment]);

	const additionalFeatures = useMemo(() => getAdditionalFeatures(highValueCarFeatures), [highValueCarFeatures]);

	const features = [...additionalFeatures, ...standardFeatures];
	const featuresCount = features.length;
	const totalCost = getTotalCost(highValueCarFeatures?.totalCost);

	return { features, featuresCount, totalCost };
};
